/**
 * Engine API client for Seitiate+.
 *
 * Wraps calls to the Seitiate engine backend (auth, tasks, settings, vault,
 * imports, creator, notifications, messaging). This repo's own backend is at
 * PLUS_API_URL and handles only dashboard-specific persistence; everything
 * else goes through the engine.
 *
 * Token key: 'cesauth_token' (shared with CES — same engine issues it).
 *
 * Migrated from: context-engine-studio/frontend/src/api/client.ts (subset)
 */

import { buildEngineHeaders, getToken, notifyUnauthorized } from '@/lib/auth';
import { SEITIATE_ENGINE_URL } from '@/lib/env';
import type {
  TaskItem,
  TaskSummary,
  TaskPromptResponse,
  SettingsResponse,
  SettingsOverviewResponse,
  SettingsCategoryResponse,
  NotificationPreferences,
  VaultEntryMetadata,
  VaultEntryListResponse,
  VaultEntryCreateRequest,
  VaultEntryUpdateRequest,
  CreatorProfile,
  MirrorReflection,
  CsvImportResponse,
  ConversationImportRequest,
  ConversationImportResponse,
  ThreadParticipant,
  InviteLinkResponse,
  WorkspaceMembershipResponse,
  Message,
  MemoRulingRequest,
  MemoRulingResponse,
  CodexAffirmationRequest,
  CodexAffirmationResponse,
  CodexAffirmationEvent,
} from './types';

// =============================================================================
// Base request helpers
// =============================================================================

class EngineApiError extends Error {
  status: number;
  detail?: string;
  constructor(message: string, status: number, detail?: string) {
    super(message);
    this.name = 'EngineApiError';
    this.status = status;
    this.detail = detail;
  }
}

function isUnavailableRouteError(error: unknown): error is EngineApiError {
  return error instanceof EngineApiError && (error.status === 404 || error.status === 500);
}

function unavailableRouteMessage(label: string): string {
  return `${label} is not available in the standalone Seitiate engine build.`;
}

function isFormDataBody(body: BodyInit | null | undefined): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options;
  let url = `${SEITIATE_ENGINE_URL}/api/v1${endpoint}`;
  if (params) {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined) sp.append(k, String(v));
    });
    const qs = sp.toString();
    if (qs) url = `${url}?${qs}`;
  }

  const headers = buildEngineHeaders({
    extra: fetchOptions.headers,
    includeContentType: true,
    body: fetchOptions.body,
  });

  const response = await fetch(url, { ...fetchOptions, headers });

  if (response.status === 204) return undefined as T;

  const raw = await response.text().catch(() => '');
  let data: unknown = undefined;
  if (raw) {
    try {
      data = JSON.parse(raw) as T;
    } catch {
      if (response.ok) {
        throw new EngineApiError('Failed to parse response', response.status, response.statusText);
      }
    }
  }

  if (!response.ok) {
    const err = data as unknown as { detail?: unknown };
    const message = (formatErrorDetail(err?.detail) ?? raw) || `Request failed: ${response.status}`;
    if (response.status === 401) {
      notifyUnauthorized(message);
    }
    throw new EngineApiError(message, response.status, message);
  }
  return data as T;
}

// FastAPI returns `detail` as a string for HTTPException, but as an array of
// `{type, loc, msg, input}` objects on 422 validation errors. The previous
// code passed the raw value to `new EngineApiError(detail, ...)`, which
// `String()`-coerced arrays to `[object Object]`. This helper produces a
// readable single-line summary regardless of shape.
function formatErrorDetail(detail: unknown): string | null {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((item) => {
      if (item && typeof item === 'object') {
        const entry = item as { msg?: unknown; loc?: unknown; type?: unknown };
        const loc = Array.isArray(entry.loc) ? entry.loc.filter((v) => v !== 'body' && v !== 'header' && v !== 'query').join('.') : '';
        const msg = typeof entry.msg === 'string' ? entry.msg : typeof entry.type === 'string' ? entry.type : JSON.stringify(item);
        return loc ? `${loc}: ${msg}` : msg;
      }
      return typeof item === 'string' ? item : JSON.stringify(item);
    });
    return parts.join('; ');
  }
  if (typeof detail === 'object') {
    try {
      return JSON.stringify(detail);
    } catch {
      return null;
    }
  }
  return String(detail);
}

function get<T>(ep: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
  return request<T>(ep, { method: 'GET', params });
}

function post<T>(ep: string, body?: unknown, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
  return request<T>(ep, {
    method: 'POST',
    body: body === undefined ? undefined : isFormDataBody(body as BodyInit) ? (body as FormData) : JSON.stringify(body),
    params,
  });
}

function put<T>(ep: string, body?: unknown): Promise<T> {
  return request<T>(ep, { method: 'PUT', body: body ? JSON.stringify(body) : undefined });
}

function patch<T>(ep: string, body: unknown): Promise<T> {
  return request<T>(ep, { method: 'PATCH', body: JSON.stringify(body) });
}

function del<T>(ep: string): Promise<T> {
  return request<T>(ep, { method: 'DELETE' });
}

// =============================================================================
// Namespaced API
// =============================================================================

export const tasks = {
  list: (filters?: Record<string, string | number | boolean>): Promise<TaskItem[]> =>
    get('/tasks/', filters),

  listAll: async (): Promise<TaskItem[]> => {
    const STATUSES = ['todo', 'in_progress', 'backlog', 'blocked', 'review', 'done', 'cancelled'] as const;
    const PRIORITIES = ['critical', 'high', 'medium', 'low'] as const;
    const buckets: Promise<TaskItem[]>[] = [];
    for (const status of STATUSES) {
      for (const priority of PRIORITIES) {
        buckets.push(tasks.list({ status, priority, limit: 200 }).catch(() => [] as TaskItem[]));
      }
    }
    const results = await Promise.all(buckets);
    const seen = new Set<string>();
    const merged: TaskItem[] = [];
    for (const bucket of results) {
      for (const t of bucket) {
        if (!seen.has(t.id)) { seen.add(t.id); merged.push(t); }
      }
    }
    return merged;
  },

  summary: (): Promise<TaskSummary> => get('/tasks/summary'),
  getTask: (id: string): Promise<TaskItem> => get(`/tasks/${id}`),
  getPrompt: (id: string): Promise<TaskPromptResponse> => get(`/tasks/${id}/prompt`),
  create: (data: Partial<TaskItem>): Promise<TaskItem> => post('/tasks/', data),
  update: (id: string, data: Partial<TaskItem>): Promise<TaskItem> => patch(`/tasks/${id}`, data),
  complete: (id: string, resolution?: string): Promise<TaskItem> =>
    post(`/tasks/${id}/complete`, { resolution: resolution || '' }),
  reseed: (): Promise<{ created: number; updated: number }> => post('/tasks/reseed', {}),
  remove: (id: string): Promise<TaskItem> => del(`/tasks/${id}`),
  dispatch: (id: string, body: { agent_type?: string; override_budget_cap_usd?: number; notes?: string } = {}): Promise<{ task: TaskItem; harness_workflow_id: string; event_stream_url: string }> =>
    post(`/tasks/${id}/dispatch`, body),
  approve: (id: string, body: { reason: string; transition_to?: string } = { reason: 'Director override' }): Promise<TaskItem> =>
    post(`/tasks/${id}/approve`, body),

  eventStreamUrl: (id: string): string => {
    const url = new URL(`${SEITIATE_ENGINE_URL}/api/v1/tasks/${encodeURIComponent(id)}/events`);
    const token = getToken();
    if (token) url.searchParams.set('token', token);
    return url.toString();
  },

  subscribeEvents: (id: string): EventSource => {
    return new EventSource(tasks.eventStreamUrl(id));
  },
};

export const settings = {
  getAll: (): Promise<SettingsResponse> => get('/settings'),
  update: (data: Record<string, unknown>): Promise<SettingsResponse> => put('/settings', data),
  getOverview: (): Promise<SettingsOverviewResponse> => get('/settings/overview'),
  getCategory: (category: string): Promise<SettingsCategoryResponse> => get(`/settings/${category}`),
  updateCategory: (category: string, payload: Record<string, unknown>): Promise<SettingsCategoryResponse> =>
    put(`/settings/${category}`, { settings: payload }),
  resetCategory: (category: string): Promise<SettingsCategoryResponse> => post(`/settings/${category}/reset`),
};

export const notifications = {
  getPreferences: (): Promise<NotificationPreferences> => get('/notifications/preferences'),
  putPreferences: (body: NotificationPreferences): Promise<NotificationPreferences> =>
    put('/notifications/preferences', body),
};

export const vault = {
  createEntry: async (data: VaultEntryCreateRequest): Promise<VaultEntryMetadata> => {
    try {
      return await post('/vault/entries', data);
    } catch (error) {
      if (isUnavailableRouteError(error)) {
        throw new EngineApiError(unavailableRouteMessage('Vault entry management'), error.status);
      }
      throw error;
    }
  },
  listEntries: async (): Promise<VaultEntryListResponse> => {
    try {
      return await get('/vault/entries');
    } catch (error) {
      if (isUnavailableRouteError(error)) {
        return { entries: [], total: 0 };
      }
      throw error;
    }
  },
  updateEntry: async (id: string, data: VaultEntryUpdateRequest): Promise<VaultEntryMetadata> => {
    try {
      return await put(`/vault/entries/${id}`, data);
    } catch (error) {
      if (isUnavailableRouteError(error)) {
        throw new EngineApiError(unavailableRouteMessage('Vault entry management'), error.status);
      }
      throw error;
    }
  },
  deleteEntry: async (id: string): Promise<void> => {
    try {
      return await del(`/vault/entries/${id}`);
    } catch (error) {
      if (isUnavailableRouteError(error)) {
        throw new EngineApiError(unavailableRouteMessage('Vault entry management'), error.status);
      }
      throw error;
    }
  },
  testEntry: async (
    id: string,
    data?: { endpoint_url?: string; timeout_ms?: number },
  ): Promise<{ entry_id: string; ok: boolean; status_code: number | null; message: string }> => {
    try {
      return await post(`/vault/entries/${id}/test`, data ?? {});
    } catch (error) {
      if (isUnavailableRouteError(error)) {
        throw new EngineApiError(unavailableRouteMessage('Vault entry testing'), error.status);
      }
      throw error;
    }
  },
};

export const creator = {
  getProfile: async (): Promise<CreatorProfile> => {
    try {
      return await get('/creator/profile');
    } catch (error) {
      if (isUnavailableRouteError(error)) {
        throw new EngineApiError(unavailableRouteMessage('Creator profile'), error.status);
      }
      throw error;
    }
  },
  getMirror: async (): Promise<MirrorReflection> => {
    try {
      return await get('/creator/mirror');
    } catch (error) {
      if (isUnavailableRouteError(error)) {
        throw new EngineApiError(unavailableRouteMessage('Creator mirror'), error.status);
      }
      throw error;
    }
  },
};

export const conversations = {
  import: (data: ConversationImportRequest): Promise<ConversationImportResponse> =>
    post('/conversations/import', data),
};

export const imports = {
  csv: (file: File, format?: string): Promise<CsvImportResponse> => {
    const fd = new FormData();
    fd.append('file', file);
    return post('/import/csv', fd, { format });
  },
  gedcom: (file: File): Promise<CsvImportResponse> => {
    const fd = new FormData();
    fd.append('file', file);
    return post('/genealogy/import-gedcom', fd);
  },
  readwise: (data: Record<string, unknown>): Promise<Record<string, unknown>> => post('/knowledge/index/readwise', data),
  roam: (jsonData: unknown): Promise<Record<string, unknown>> => post('/knowledge/index/roam', { json_data: jsonData }),
  logseq: (jsonData: unknown): Promise<Record<string, unknown>> => post('/knowledge/index/logseq', { json_data: jsonData }),
  obsidian: (vaultPath: string): Promise<Record<string, unknown>> => post('/knowledge/index/obsidian', { vault_path: vaultPath }),
  mediumExport: (file: File): Promise<Record<string, unknown>> => {
    const fd = new FormData();
    fd.append('file', file);
    return post('/sources/medium/export', fd);
  },
  mediumRss: (feedUrls: string[]): Promise<Record<string, unknown>> => post('/sources/medium/rss', { feed_urls: feedUrls }),
  mediumArticles: (data: Record<string, unknown>): Promise<Record<string, unknown>> => post('/sources/medium/api/articles', data),
};

// Messaging API (for board share modal, comment thread)
export const messaging = {
  authHeaders: (): HeadersInit => {
    return buildEngineHeaders({ includeContentType: true });
  },
};

// ── Memos (BOARD-RULING-MODAL-1) ─────────────────────────────────────────
//
// The Seitiate backend (~/Dev/seitiate/app/api/v1/memos.py) exposes a
// memo ruling endpoint that updates the memo file's director_ruling
// field, marks the corresponding RULING-* task done, and strips
// blocked_on:<question_id> tags from downstream tasks. See types.ts
// for the request/response schemas.

export const memos = {
  submitRuling: async (
    request: MemoRulingRequest,
  ): Promise<MemoRulingResponse> => {
    return await post('/memos/ruling', request);
  },
};

// ── Inbox (WEB-APP-INBOX-V0-1) ────────────────────────────────────────────
//
// Director's "Send to seity" entry point. POSTs land in a per-user-per-lens
// Communication OS thread on the engine; agent-side processing is V1 work.

export interface InboxSubmitRequest {
  content: string;
  lens?: string | null;
  source_url?: string | null;
}

export interface InboxSubmitResponse {
  thread_id: string;
  message_id: string;
  lens: string;
  thread_name: string;
  created_at: string;
}

export interface InboxMessageRow {
  message_id: string;
  thread_id: string;
  thread_name: string | null;
  lens: string;
  content_preview: string;
  source_url: string | null;
  created_at: string;
}

export const inbox = {
  submit: (payload: InboxSubmitRequest): Promise<InboxSubmitResponse> =>
    post('/inbox/messages', payload),
  listRecent: (limit = 10, lens?: string): Promise<InboxMessageRow[]> =>
    get('/inbox/messages', { limit, ...(lens ? { lens } : {}) }),
};

// ── Codex affirmation (Seity Wave 1 W1.4) ────────────────────────────────
//
// A person affirms, corrects, or rejects one recalled Codex memory; the
// engine records one `codex_affirmation` event with the reason sealed.
// The codex router is mounted at /codex (seitiate/app/api/v1/router.py).

export function affirmCodexChunk(
  chunkId: string,
  payload: CodexAffirmationRequest,
): Promise<CodexAffirmationResponse> {
  return post(`/codex/chunks/${encodeURIComponent(chunkId)}/affirmation`, payload);
}

export function listCodexChunkAffirmations(chunkId: string): Promise<CodexAffirmationEvent[]> {
  return get(`/codex/chunks/${encodeURIComponent(chunkId)}/affirmation`);
}

export const codex = {
  affirmChunk: affirmCodexChunk,
  listChunkAffirmations: listCodexChunkAffirmations,
};

// Convenience wrapper matching the CES `api` default export shape
const api = {
  tasks,
  settings,
  notifications,
  vault,
  imports,
  conversations,
  creator,
  memos,
  inbox,
  codex,
};

export default api;

// Re-export the task comment and workspace APIs for the board components
export const taskComments = {
  list: (taskId: string): Promise<Message[]> => get(`/messaging/threads/task/${taskId}/messages`),
  post: (taskId: string, content: string): Promise<Message> =>
    post(`/messaging/threads/task/${taskId}/messages`, { content_text: content }),
};

/**
 * Workspaces API — wired to the Seitiate engine workspace endpoints
 * mounted at `/api/v1/workspaces/{thread_id}/...`.
 *
 * NOTE: the previous `/messaging/workspaces/...` namespace was wrong —
 * the engine never exposed that prefix. See BOARD-SHARE-INVITE-FLOW-REPAIR-1.
 */
export const workspaces = {
  listMembers: (wsId: string): Promise<ThreadParticipant[]> =>
    get(`/workspaces/${wsId}/members`),
  inviteByEmail: (wsId: string, email: string, role: string): Promise<ThreadParticipant> =>
    post(`/workspaces/${wsId}/invite`, { email, role }),
  createInviteLink: (
    wsId: string,
    role: string,
    expiresInHours = 168,
  ): Promise<InviteLinkResponse> =>
    post(`/workspaces/${wsId}/invite-link`, { role, expires_in_hours: expiresInHours }),
  addDelegatedAgent: (
    wsId: string,
    agentId: string,
    displayName?: string,
  ): Promise<ThreadParticipant> =>
    post(`/workspaces/${wsId}/agents`, { agent_id: agentId, display_name: displayName || null }),
  addBondedFamili: (
    wsId: string,
    familiName: string,
    displayName?: string,
  ): Promise<ThreadParticipant> =>
    post(`/workspaces/${wsId}/familis`, { famili_name: familiName, display_name: displayName || null }),
  acceptInvite: (token: string): Promise<WorkspaceMembershipResponse> =>
    post(`/workspaces/join/${encodeURIComponent(token)}`),
  updateRole: (wsId: string, userId: string, role: string): Promise<ThreadParticipant> =>
    patch(`/workspaces/${wsId}/members/${userId}`, { role }),
  removeMember: (wsId: string, userId: string): Promise<void> =>
    del(`/workspaces/${wsId}/members/${userId}`),
};

export { EngineApiError };
